import React from 'react';
import {Card, Grid, Typography} from 'antd';

const {Title, Paragraph, Text, Link} = Typography;
const {useBreakpoint} = Grid;

const PrivacyPolicyPage: React.FC = () => {
    const screens = useBreakpoint();

    return (
        <div style={{
            padding: screens.md ? '24px' : '16px',
            maxWidth: '800px',
            margin: '0 auto',
        }}>
            <Card
                style={{ borderRadius: '12px' }}
                styles={{ body: { padding: screens.md ? '32px' : '20px' } }}
            >
            <Title level={2}>Privacy Policy</Title>
            <Text type="secondary">Last updated: April 2026</Text>

            <Title level={4} style={{marginTop: '32px'}}>1. Introduction</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                MetaGame News ("we", "our", "us") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, and safeguard your information
                when you visit our website and use our mobile applications.
            </Paragraph>

            <Title level={4}>2. Information We Collect</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                <strong>Automatically collected:</strong> When you visit our site, we may collect
                certain information automatically, including your IP address, browser type,
                operating system, referring URLs, and browsing behavior through cookies and
                similar technologies.
            </Paragraph>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                <strong>Account information:</strong> If you create an account, we collect your
                name, email address, and authentication credentials. If you sign in via a
                third-party provider (Google, LinkedIn, Apple), we receive basic profile
                information as authorized by you.
            </Paragraph>

            <Title level={4}>3. How We Use Your Information</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                We use the information we collect to:
            </Paragraph>
            <ul style={{fontSize: '15px', lineHeight: '2'}}>
                <li>Provide and improve our news aggregation services</li>
                <li>Personalize your news experience based on your preferences</li>
                <li>Analyze usage patterns to improve our platform</li>
                <li>Send important service-related communications</li>
                <li>Comply with legal obligations</li>
            </ul>

            <Title level={4}>4. Cookies and Tracking</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                We use cookies and similar technologies for essential site functionality,
                remembering your preferences (such as theme and language), and analytics.
                You can manage cookie preferences through your browser settings or our
                cookie consent banner.
            </Paragraph>

            <Title level={4}>5. Data Sharing</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                We do not sell your personal information. We may share data with trusted
                third-party service providers who assist us in operating our platform
                (e.g., hosting, analytics), subject to strict data protection agreements.
            </Paragraph>

            <Title level={4}>6. Your Rights (GDPR)</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                If you are located in the European Economic Area, you have the right to:
            </Paragraph>
            <ul style={{fontSize: '15px', lineHeight: '2'}}>
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to or restrict processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
            </ul>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                To exercise any of these rights, please contact us
                at <Link href="mailto:contact@metagame.news">contact@metagame.news</Link>.
            </Paragraph>

            <Title level={4}>7. Data Retention</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                We retain personal data only as long as necessary for the purposes outlined
                in this policy, or as required by law. You may request deletion of your
                account and associated data at any time.
            </Paragraph>

            <Title level={4}>8. Security</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                We implement appropriate technical and organizational measures to protect
                your personal data against unauthorized access, alteration, disclosure, or
                destruction.
            </Paragraph>

            <Title level={4}>9. Changes to This Policy</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                We may update this Privacy Policy from time to time. We will notify you of
                any material changes by posting the updated policy on this page with a new
                "Last updated" date.
            </Paragraph>

            <Title level={4}>10. Contact</Title>
            <Paragraph style={{fontSize: '15px', lineHeight: '1.8'}}>
                If you have any questions about this Privacy Policy, please contact us
                at <Link href="mailto:contact@metagame.news">contact@metagame.news</Link>.
            </Paragraph>
            </Card>
        </div>
    );
};

export default PrivacyPolicyPage;
